/**
 * SubGeomEditorModal — editor visual del `sub_geom` de una plantación
 * doblada en modo parcial. Se abre desde el botón "Dibujar sub-área"
 * de `PlantingCardInline` cuando la parcela está en modo "Parcial".
 *
 * Fase C del refactor de doblados web (paridad con la app móvil).
 * En móvil este flujo vive en `DrawPlantingSubGeomScreen`; aquí lo
 * envolvemos en un modal AntD con un `<MapContainer>` de react-leaflet
 * + `leaflet-draw` vanilla (mismo patrón que `PlotMapEditor.tsx` —
 * evitamos añadir la dependencia `react-leaflet-draw`).
 *
 * Reglas:
 *   · El polígono del padre (`AuditPlot.geom`) se pinta como referencia
 *     read-only en verde tenue. El auditor NO puede editarlo desde
 *     aquí — sólo dibuja el sub-polígono dentro de él.
 *   · Los sub_geoms existentes en otras plantings del mismo plot se
 *     pintan en gris (también read-only) — evita solaparse por
 *     accidente y ayuda a hueco visualmente.
 *   · Al guardar, validamos que:
 *       (a) el sub-polígono está contenido dentro del padre;
 *       (b) no solapa con ninguno de los sub_geoms hermanos.
 *     Si alguna validación falla, se muestra un `<Alert>` con el motivo
 *     y no se persiste.
 *   · Podemos borrar el sub_geom actual (deja la planting sin sub-área,
 *     lo que en la práctica vuelve a doblado total para esa planting).
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { Alert, App as AntdApp, Button, Modal, Space, Typography } from "antd";
import { MapContainer, Polygon, TileLayer, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet-draw/dist/leaflet.draw.css";
// Dynamic import de leaflet-draw — evita issues con Vite y bundle
// static analysis (mismo patrón que PlotMapEditor.tsx).
if (typeof window !== "undefined") {
  void import("leaflet-draw");
}
import booleanWithin from "@turf/boolean-within";
import intersect from "@turf/intersect";
import { polygon as turfPolygon, featureCollection } from "@turf/helpers";

import type { AuditPlotPlanting } from "@/types/audit";
import { computeGeodesicAreaHa } from "@/utils/geo";

const { Text } = Typography;

interface Props {
  open: boolean;
  parentGeom: GeoJSON.Geometry | null;
  planting: AuditPlotPlanting;
  siblings: AuditPlotPlanting[];
  onCancel: () => void;
  onSave: (subGeom: GeoJSON.Geometry, surfaceHa: number) => Promise<void>;
  onClear: () => Promise<void>;
}

/** Convierte una `GeoJSON.Geometry` en una lista de anillos exteriores
 *  (`LatLngExpression[][]`) que react-leaflet Polygon entiende.
 *  Soporta Polygon y MultiPolygon; devuelve [] en cualquier otro caso. */
function geomToLeafletCoords(
  geom: GeoJSON.Geometry | null,
): L.LatLngExpression[][] {
  if (!geom) return [];
  if (geom.type === "Polygon") {
    return [
      (geom.coordinates[0] as number[][]).map(([lon, lat]) => [lat, lon]),
    ];
  }
  if (geom.type === "MultiPolygon") {
    return (geom.coordinates as number[][][][]).map((poly) =>
      (poly[0] as number[][]).map(([lon, lat]) => [lat, lon]),
    );
  }
  return [];
}

/** Extrae el bounding box de una geometría para centrar el mapa. */
function geomBounds(
  geom: GeoJSON.Geometry | null,
): L.LatLngBoundsExpression | null {
  const rings = geomToLeafletCoords(geom);
  if (rings.length === 0) return null;
  const all = rings.flat();
  if (all.length === 0) return null;
  const lats = all.map((p) => (p as [number, number])[0]);
  const lngs = all.map((p) => (p as [number, number])[1]);
  return [
    [Math.min(...lats), Math.min(...lngs)],
    [Math.max(...lats), Math.max(...lngs)],
  ];
}

/**
 * Componente interno que hookea el Draw control de leaflet-draw
 * (vanilla) al MapContainer padre. Mantiene UN único polígono en el
 * FeatureGroup — si el auditor dibuja uno nuevo, borra el anterior.
 */
const DrawControls: React.FC<{
  initialGeom: GeoJSON.Geometry | null;
  onGeomChange: (geom: GeoJSON.Geometry | null) => void;
}> = ({ initialGeom, onGeomChange }) => {
  const map = useMap();
  const drawnItemsRef = useRef<L.FeatureGroup>(new L.FeatureGroup());
  const seededRef = useRef(false);

  useEffect(() => {
    const drawnItems = drawnItemsRef.current;
    map.addLayer(drawnItems);

    // Sembramos el sub_geom existente (si lo hay) para que aparezca
    // en el editor con handles. Sólo la primera vez — cambios
    // posteriores del `initialGeom` (p. ej. modal reabierto) se
    // gestionan re-montando el componente vía `key` en el caller.
    if (!seededRef.current && initialGeom) {
      const rings = geomToLeafletCoords(initialGeom);
      rings.forEach((ring) => {
        const layer = L.polygon(ring, {
          color: "#fa8c16",
          weight: 2,
          fillColor: "#ffd591",
          fillOpacity: 0.4,
        });
        drawnItems.addLayer(layer);
      });
      seededRef.current = true;
    }

    const drawControl = new L.Control.Draw({
      position: "topright",
      edit: { featureGroup: drawnItems, remove: true },
      draw: {
        polygon: {
          allowIntersection: false,
          showArea: false,
          shapeOptions: {
            color: "#fa8c16",
            weight: 2,
            fillColor: "#ffd591",
            fillOpacity: 0.4,
          },
          drawError: { color: "#e74c3c", timeout: 1000 },
        },
        polyline: false,
        rectangle: false,
        circle: false,
        marker: false,
        circlemarker: false,
      },
    });
    map.addControl(drawControl);

    // Al crear un polígono nuevo, borramos los previos — sólo mantenemos
    // uno. leaflet-draw ya añade el nuevo layer al FeatureGroup.
    map.on(L.Draw.Event.CREATED, (event: any) => {
      const layer = event.layer as L.Polygon;
      const toRemove: L.Layer[] = [];
      drawnItems.eachLayer((l) => {
        if (l !== layer) toRemove.push(l);
      });
      toRemove.forEach((l) => drawnItems.removeLayer(l));
      drawnItems.addLayer(layer);
      const gj = layer.toGeoJSON() as GeoJSON.Feature<GeoJSON.Polygon>;
      onGeomChange(gj.geometry);
    });

    map.on(L.Draw.Event.EDITED, () => {
      const layers = drawnItems.getLayers();
      if (layers.length === 0) {
        onGeomChange(null);
        return;
      }
      const gj = (layers[0] as L.Polygon).toGeoJSON() as GeoJSON.Feature<GeoJSON.Polygon>;
      onGeomChange(gj.geometry);
    });

    map.on(L.Draw.Event.DELETED, () => {
      onGeomChange(null);
    });

    return () => {
      map.removeControl(drawControl);
      map.removeLayer(drawnItems);
      map.off(L.Draw.Event.CREATED);
      map.off(L.Draw.Event.EDITED);
      map.off(L.Draw.Event.DELETED);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map]);

  return null;
};

export const SubGeomEditorModal: React.FC<Props> = ({
  open,
  parentGeom,
  planting,
  siblings,
  onCancel,
  onSave,
  onClear,
}) => {
  const { message } = AntdApp.useApp();
  const [draftGeom, setDraftGeom] = useState<GeoJSON.Geometry | null>(
    planting.sub_geom ?? null,
  );
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Cuando cambia la planting seleccionada o el modal se reabre,
  // resetamos el borrador al valor persistido. El `key` en el
  // MapContainer también fuerza remount del editor para reseed.
  useEffect(() => {
    if (open) {
      setDraftGeom(planting.sub_geom ?? null);
      setError(null);
    }
  }, [planting.id, planting.sub_geom, open]);

  const parentRings = useMemo(
    () => geomToLeafletCoords(parentGeom),
    [parentGeom],
  );
  const bounds = useMemo(() => geomBounds(parentGeom), [parentGeom]);

  const siblingGeoms = useMemo(
    () =>
      siblings
        .filter((s) => s.id !== planting.id && s.sub_geom != null)
        .map((s) => ({ id: s.id, ordering: s.ordering, geom: s.sub_geom! })),
    [siblings, planting.id],
  );

  const suggestedAreaHa = useMemo(
    () => computeGeodesicAreaHa(draftGeom),
    [draftGeom],
  );

  const validate = (geom: GeoJSON.Geometry): string | null => {
    if (!parentGeom) {
      return "La parcela padre no tiene geometría — dibuja primero el polígono principal.";
    }
    if (geom.type !== "Polygon") {
      return "El sub-área debe ser un único polígono.";
    }
    try {
      const sub = turfPolygon(
        (geom as GeoJSON.Polygon).coordinates as number[][][],
      );
      const parentPolys: GeoJSON.Polygon[] = [];
      if (parentGeom.type === "Polygon") {
        parentPolys.push(parentGeom as GeoJSON.Polygon);
      } else if (parentGeom.type === "MultiPolygon") {
        for (const poly of (parentGeom as GeoJSON.MultiPolygon).coordinates) {
          parentPolys.push({ type: "Polygon", coordinates: poly });
        }
      } else {
        return "La geometría de la parcela no es un polígono válido.";
      }
      const containedInSome = parentPolys.some((p) => {
        try {
          return booleanWithin(
            sub,
            turfPolygon(p.coordinates as number[][][]),
          );
        } catch {
          return false;
        }
      });
      if (!containedInSome) {
        return "El sub-área debe estar completamente contenida dentro de la parcela padre.";
      }

      for (const s of siblingGeoms) {
        if (s.geom.type !== "Polygon") continue;
        try {
          const sib = turfPolygon(
            (s.geom as GeoJSON.Polygon).coordinates as number[][][],
          );
          const inter = intersect(featureCollection([sub, sib]));
          if (inter && inter.geometry) {
            const areaHa =
              computeGeodesicAreaHa(inter.geometry as GeoJSON.Geometry) ?? 0;
            if (areaHa > 0.0001) {
              return `El sub-área solapa con la plantación #${s.ordering}. Ajústala para no invadirla.`;
            }
          }
        } catch {
          /* ignoramos errores puntuales de turf en geometrías raras */
        }
      }
    } catch {
      return "No pudimos validar la geometría — inténtalo de nuevo.";
    }
    return null;
  };

  const handleSave = async () => {
    if (!draftGeom) {
      setError("Dibuja primero un sub-área.");
      return;
    }
    const validationError = validate(draftGeom);
    if (validationError) {
      setError(validationError);
      return;
    }
    setSaving(true);
    try {
      const areaHa = computeGeodesicAreaHa(draftGeom) ?? 0;
      await onSave(draftGeom, Number(areaHa.toFixed(4)));
      message.success("Sub-área guardada.");
    } catch (err) {
      const detail =
        (err as { response?: { data?: unknown } }).response?.data ??
        (err as Error).message;
      setError(`Error al guardar: ${JSON.stringify(detail)}`);
    } finally {
      setSaving(false);
    }
  };

  const handleClear = async () => {
    setSaving(true);
    try {
      await onClear();
      message.success("Sub-área eliminada.");
    } catch (err) {
      const detail =
        (err as { response?: { data?: unknown } }).response?.data ??
        (err as Error).message;
      setError(`Error al eliminar: ${JSON.stringify(detail)}`);
    } finally {
      setSaving(false);
    }
  };

  // Key del MapContainer atado al id + versión de sub_geom: garantiza
  // que si el auditor reabre el modal con una planting distinta, el
  // DrawControls se re-monta con el estado correcto (evita quedar
  // pegado a la geometría anterior).
  const mapKey = `${planting.id}-${planting.sub_geom ? "with" : "none"}-${open}`;

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      title={`Sub-área de la plantación #${planting.ordering}`}
      width={860}
      footer={
        <Space>
          <Button onClick={onCancel}>Cancelar</Button>
          {planting.sub_geom ? (
            <Button danger loading={saving} onClick={handleClear}>
              Eliminar sub-área
            </Button>
          ) : null}
          <Button
            type="primary"
            loading={saving}
            onClick={handleSave}
            disabled={!draftGeom}
          >
            Guardar
          </Button>
        </Space>
      }
      destroyOnClose
    >
      {!parentGeom ? (
        <Alert
          type="warning"
          showIcon
          message="La parcela padre no tiene geometría. Dibuja primero el polígono principal antes de definir sub-áreas."
        />
      ) : (
        <>
          <Text type="secondary" style={{ display: "block", marginBottom: 8 }}>
            Dibuja un polígono dentro de la parcela. Se validará que quede
            contenido en ella y que no solape con las plantaciones hermanas.
          </Text>
          {suggestedAreaHa != null ? (
            <Text style={{ display: "block", marginBottom: 8 }}>
              <strong>Superficie del sub-área:</strong>{" "}
              {suggestedAreaHa.toFixed(4)} ha
            </Text>
          ) : null}
          {error ? (
            <Alert
              type="error"
              showIcon
              closable
              style={{ marginBottom: 8 }}
              message={error}
              onClose={() => setError(null)}
            />
          ) : null}
          <div style={{ height: 480, borderRadius: 4, overflow: "hidden" }}>
            <MapContainer
              key={mapKey}
              bounds={bounds ?? undefined}
              style={{ height: "100%", width: "100%" }}
              scrollWheelZoom
            >
              <TileLayer
                attribution="&copy; OpenStreetMap contributors"
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              {parentRings.map((ring, i) => (
                <Polygon
                  key={`parent-${i}`}
                  positions={ring}
                  pathOptions={{
                    color: "#52c41a",
                    weight: 2,
                    fillColor: "#b7eb8f",
                    fillOpacity: 0.15,
                  }}
                  interactive={false}
                />
              ))}
              {siblingGeoms.map((s, i) => {
                const ring = geomToLeafletCoords(s.geom)[0];
                if (!ring) return null;
                return (
                  <Polygon
                    key={`sib-${s.id}-${i}`}
                    positions={ring}
                    pathOptions={{
                      color: "#8c8c8c",
                      weight: 1.5,
                      dashArray: "4 4",
                      fillColor: "#bfbfbf",
                      fillOpacity: 0.2,
                    }}
                    interactive={false}
                  />
                );
              })}
              <DrawControls
                initialGeom={planting.sub_geom ?? null}
                onGeomChange={(g) => {
                  setDraftGeom(g);
                  setError(null);
                }}
              />
            </MapContainer>
          </div>
        </>
      )}
    </Modal>
  );
};
